import { sumObjectArrayProperty } from '../utils/sumObjectArrayProperty'
import { doesQualifyToBeHidden } from './ItemAvailabilityStatus'
import {
  getUniqueSuppliersFromMSServices,
  isMSServiceWithProductLevelItem,
} from './MSService'
import { countUniqueStatii } from './StatiiCounts'
import { getTermBreakdown } from './TermBreakdown'
import { countUniqueLocations } from './LocationCounts'
import {
  getCountableMSServices,
  isCountableMSService,
  isSupersededMSService,
} from './MSServices'
import type {
  ICompany,
  ICompanyMasterService,
  ICompanyMSService,
  ICompanyOverview,
} from '~/types/company/models'
import type { IEntityDataItemWithStatus, TEntityId } from '~/types/models'
import type { TMSService } from '~/types/msService/models'
import type { IContract } from '~/types/contract/models'
import {
  CompanyMasterServicesStatusIds,
  getCompanyMasterServiceStatusById,
} from '~/services/selectLists/companyMasterServicesStatii'
import { CompanyMSServicesStatusIds } from '~/services/selectLists/companyMSServicesStatii'
import {
  ContractStatusIds,
  getContractStatusById,
} from '~/services/selectLists/contractStatii'
import { ContractTypeIds } from '~/services/selectLists/contractTypes'

const mn = '[cynqueModules/dataObjects/CompanyMSService.ts]'

export function isInstalledCompanyMSService(
  companyMSService: ICompanyMSService
): boolean {
  return (
    companyMSService.status.id === CompanyMSServicesStatusIds.ACTIVE ||
    companyMSService.status.id === CompanyMSServicesStatusIds.SUSPENDED ||
    companyMSService.status.id === CompanyMSServicesStatusIds.DISCONNECTED ||
    companyMSService.status.id === CompanyMSServicesStatusIds.REACTIVATED
  )
}

interface TPotentialStatus {
  // `number`, not the boxed `Number` — the sibling fields below already use the
  // primitive, and `Number` is the wrapper-object type (no arithmetic, flagged
  // by ban-types).
  statusId: number
  alternateStatusId?: number
  didFindStatus?: boolean
  didFindOtherThanStatusOrCancelled?: boolean
  statusIdForOnlyMssStatus: number
  statusIdForOtherFound: number
}

/**
 * The msServices whose amounts roll up into a parent: included, and not
 * DISCONNECTED. Extracted so the company-level and master-service-level
 * roll-ups filter once and share the subset across all their sums instead of
 * re-filtering the array per amount.
 *
 * ⚠ Also excludes every row that qualifies to be hidden — not available,
 * declined, swapped out, removed. A hidden msService keeps its amounts, so
 * without this the money it represents keeps counting on a row that is not part
 * of the configuration.
 *
 * 🔑 `doesQualifyToBeHidden`, NOT `isSupersededMSService`. Those differ by the
 * two legacy states — NOT_AVAILABLE and OPTIONAL_NOT_INCLUDED — and this path
 * used to admit both, while the kit and card totals excluded them. So the two
 * client money paths disagreed with each other about a declined row, and the
 * narrower test here is what made that true. The API's amount views take the
 * same widening, so client and server agree on one rule: hidden is not counted.
 *
 * ⚠ WHAT THIS DOES AND DOES NOT MOVE. MRC does not move at all: a hidden row is
 * quantity-zeroed by `deriveMSServiceQuantity` and mrcAmount is quantity x rate,
 * so it was already contributing zero. NRC, one-time and TCV are the real delta
 * — none of them passes through quantity (nrcAmount is `1 x unitNrcAmount`
 * whenever doMultiplyNrcByQuantity is false, oneTimeAmount is stored outright,
 * and tcvAmount collapses to oneTimeAmount once mrcAmount is zero). ⚠ The mock
 * fixture has every unitNrcAmount and oneTimeAmount at zero, so a green suite
 * says nothing here; the delta has to be measured against real data.
 */
function getMSServicesToRollUp(msServices: TMSService[]): TMSService[] {
  return msServices.filter(
    (msService) =>
      msService.doInclude &&
      msService.status.id !== CompanyMSServicesStatusIds.DISCONNECTED &&
      !doesQualifyToBeHidden(msService)
  )
}

export async function propagateCMSServicesData({
  overviewData,
  allCMSServices,
  setUpCompanyMSServices,
}: {
  overviewData: ICompanyOverview
  allCMSServices: TMSService[]
  setUpCompanyMSServices: Function
}) {
  // embedded functions
  function _propagateFromCMSServicesToCompany({
    mn,
    company,
    allCMSServices,
  }: {
    mn: string
    company: ICompany
    allCMSServices: TMSService[]
  }) {
    function __getRecursPaidDescr(allCMSServices: TMSService[]) {
      // One pass, short-circuiting on the first disagreement. This used to
      // `find` the reference value and then re-`filter` the whole array with the
      // identical predicate before `every` — three traversals and a throwaway
      // array to answer one question.
      let firstValidRecursPaidDescr: string | undefined
      for (const cmss of allCMSServices) {
        if (
          !cmss.doInclude ||
          cmss.recursPaidDescr === '--' ||
          doesQualifyToBeHidden(cmss)
        ) {
          continue
        }
        if (firstValidRecursPaidDescr === undefined) {
          firstValidRecursPaidDescr = cmss.recursPaidDescr
        } else if (cmss.recursPaidDescr !== firstValidRecursPaidDescr) {
          return 'Varies'
        }
      }
      return firstValidRecursPaidDescr ?? '--'
    }

    //
    // Execution starts here
    //

    const fn = `${mn} _propagateFromCMSServicesToCompany |`

    // Not propagating status changes to Company. (We do do this
    // for Opportunity and Order, but not Company.)
    // // Make sure we have some services to propagate from
    // if (!allCMSServices?.length) {
    //   return
    // }
    // const didFindFirstCMSSWIthProductLevelItem =
    //   allCMSServices.find(
    //     (cmss: TMSService) => cmss.product.level.id === ProductLevelIds.ITEM
    //     // A KIT product with no ITEM-level products is not valid
    //   ) != undefined
    // if (!didFindFirstCMSSWIthProductLevelItem) {
    //   return
    // }

    // // Propagate Status
    // const propagateStatusArray: TPotentialStatus[] = [
    //   {
    //     statusId: CompanyMSServicesStatusIds.IN_REVIEW,
    //     statusIdForOnlyMssStatus: CompanyStatusIds.IN_REVIEW,
    //     statusIdForOtherFound: CompanyStatusIds.SOME_IN_REVIEW,
    //   },
    //   {
    //     statusId: CompanyMSServicesStatusIds.READY_FOR_CONTRACT,
    //     statusIdForOnlyMssStatus: CompanyStatusIds.READY_FOR_CONTRACTS,
    //     statusIdForOtherFound: CompanyStatusIds.SOME_READY_FOR_CONTRACTS,
    //   },
    //   {
    //     statusId: CompanyMSServicesStatusIds.ACTIVE,
    //     statusIdForOnlyMssStatus: CompanyStatusIds.ACTIVE,
    //     statusIdForOtherFound: CompanyStatusIds.SOME_COMPLETE,
    //   },
    //   {
    //     statusId: CompanyMSServicesStatusIds.DISCONNECTED,
    //     statusIdForOnlyMssStatus: CompanyStatusIds.DISCONNECTED,
    //     statusIdForOtherFound: CompanyStatusIds.SOME_DISCONNECTED,
    //   },
    // ]

    // company.status = getTargetEntityStatusFromPropagateStatusArray<ICompany>({
    //   propagateStatusArray,
    //   cmssArray: allCMSServices,
    //   targetEntity: company,
    //   targetEntityGetStatusByIdFunction: getCompanyStatusById,
    // })

    // Propagate other than status.
    // All four amounts share one predicate, so filter ONCE and sum the subset,
    // rather than re-filtering the whole array inside each sum (which cost
    // eight full passes to answer four questions).
    const cmsServicesToRollUp = getMSServicesToRollUp(allCMSServices)
    company.mrcAmount = sumObjectArrayProperty(
      fn,
      cmsServicesToRollUp,
      'mrcAmount'
    )
    console.debug(`${fn} mrcAnount: ${company.mrcAmount}`)
    company.nrcAmount = sumObjectArrayProperty(
      fn,
      cmsServicesToRollUp,
      'nrcAmount'
    )
    company.oneTimeAmount = sumObjectArrayProperty(
      fn,
      cmsServicesToRollUp,
      'oneTimeAmount'
    )
    company.tcvAmount = sumObjectArrayProperty(
      fn,
      cmsServicesToRollUp,
      'tcvAmount'
    )

    company.recursPaidDescr = __getRecursPaidDescr(allCMSServices)
    company.suppliers = getUniqueSuppliersFromMSServices(allCMSServices)

    const filteredCompanyMSServices = getCountableMSServices(allCMSServices)
    company.numCompanyMSServices = filteredCompanyMSServices.length
    company.cmssStatusCounts = countUniqueStatii(filteredCompanyMSServices)
    company.cmssLocationCounts = countUniqueLocations(filteredCompanyMSServices)
  }
  function _propagateFromCMSServicesToCMS({
    mn,
    allCMS,
  }: {
    mn: string
    allCMS: ICompanyMasterService[]
  }) {
    const fn = `${mn} _propagateFromCMSServicesToCMS |`
    allCMS.forEach((cms: ICompanyMasterService) => {
      console.debug(`${fn} cms: ${cms.descr}`)

      // Make sure we have some services to propagate from
      const indexOfFirstCMSSWIthProductLevelItem =
        cms.companyMSServices.findIndex(isMSServiceWithProductLevelItem)
      if (indexOfFirstCMSSWIthProductLevelItem === -1) {
        cms.term = '--'
        cms.termBreakdown = undefined
        cms.status = getCompanyMasterServiceStatusById(
          CompanyMasterServicesStatusIds.NO_SERVICES
        )
        return
      }

      // Propagate Status
      const propagateStatusArray: TPotentialStatus[] = [
        {
          statusId: CompanyMSServicesStatusIds.IN_REVIEW,
          statusIdForOnlyMssStatus: CompanyMasterServicesStatusIds.IN_REVIEW,
          statusIdForOtherFound: CompanyMasterServicesStatusIds.SOME_IN_REVIEW,
        },
        {
          statusId: CompanyMSServicesStatusIds.READY_FOR_CONTRACT,
          statusIdForOnlyMssStatus:
            CompanyMasterServicesStatusIds.READY_FOR_CONTRACTS,
          statusIdForOtherFound:
            CompanyMasterServicesStatusIds.SOME_READY_FOR_CONTRACTS,
        },
        {
          statusId: CompanyMSServicesStatusIds.ASSIGNED,
          statusIdForOnlyMssStatus: CompanyMasterServicesStatusIds.ASSIGNED,
          statusIdForOtherFound: CompanyMasterServicesStatusIds.SOME_ASSIGNED,
        },
        {
          statusId: CompanyMSServicesStatusIds.ACTIVE,
          alternateStatusId: CompanyMSServicesStatusIds.REACTIVATED,
          statusIdForOnlyMssStatus: CompanyMasterServicesStatusIds.ACTIVE,
          statusIdForOtherFound: CompanyMasterServicesStatusIds.SOME_ACTIVE,
        },
        {
          statusId: CompanyMSServicesStatusIds.SUSPENDED,
          statusIdForOnlyMssStatus: CompanyMasterServicesStatusIds.SUSPENDED,
          statusIdForOtherFound: CompanyMasterServicesStatusIds.SOME_SUSPENDED,
        },
        {
          statusId: CompanyMSServicesStatusIds.DISCONNECTED,
          statusIdForOnlyMssStatus: CompanyMasterServicesStatusIds.DISCONNECTED,
          statusIdForOtherFound:
            CompanyMasterServicesStatusIds.SOME_DISCONNECTED,
        },
      ]

      cms.status =
        getTargetEntityStatusFromPropagateStatusArray<ICompanyMasterService>({
          propagateStatusArray,
          cmssArray: cms.companyMSServices,
          targetEntity: cms,
          targetEntityGetStatusByIdFunction: getCompanyMasterServiceStatusById,
        })

      // Propagate financial data
      // Counted over exactly the # Srvs set, so the Term hover's counts add up
      // to that column. `term` stays 'Varies' when they differ; the card
      // shows `termBreakdown.majorityTerm`.
      const termBreakdown = getTermBreakdown(
        getCountableMSServices(cms.companyMSServices)
      )
      cms.term = termBreakdown.term
      cms.termBreakdown = termBreakdown.doesVary ? termBreakdown : undefined
      const cmsServicesToRollUp = getMSServicesToRollUp(cms.companyMSServices)
      cms.mrcAmount = sumObjectArrayProperty(
        fn,
        cmsServicesToRollUp,
        'mrcAmount'
      )
      console.debug(`${fn} mrcAnount: ${cms.mrcAmount}`)
      cms.nrcAmount = sumObjectArrayProperty(
        fn,
        cmsServicesToRollUp,
        'nrcAmount'
      )
      cms.oneTimeAmount = sumObjectArrayProperty(
        fn,
        cmsServicesToRollUp,
        'oneTimeAmount'
      )

      // counts
      const filteredCMSServices = getCountableMSServices(cms.companyMSServices)
      cms.numServices = filteredCMSServices?.length ?? 0
      cms.numLocations = new Set(
        filteredCMSServices.map((cmss) => cmss.location.id)
      ).size

      cms.cmssStatusCounts = countUniqueStatii(filteredCMSServices)
      cms.cmssLocationCounts = countUniqueLocations(filteredCMSServices)

      console.debug(
        `${fn} ${cms.descr}: ${cms.cmssStatusCounts.length} status buckets,` +
          ` ${cms.cmssLocationCounts.length} location buckets`
      )
    })
  }
  function _propagateFromCMSServicesToContracts({
    mn,
    allContracts,
    allCMSServices,
  }: {
    mn: string
    allContracts: IContract[]
    allCMSServices: TMSService[]
  }) {
    const fn = `${mn} _propagateFromCMSServicesToContracts |`

    // Index the countable msServices by contract id in ONE pass. This used to
    // filter the entire array once per contract — O(contracts x msServices) —
    // which is the term that hurts on a company with many contracts.
    const countableCMSServicesByContractId = new Map<TEntityId, TMSService[]>()
    for (const cmss of allCMSServices) {
      const contractId = cmss.contract?.id
      if (contractId === undefined || !isCountableMSService(cmss)) {
        continue
      }
      const bucket = countableCMSServicesByContractId.get(contractId)
      if (bucket) {
        bucket.push(cmss)
      } else {
        countableCMSServicesByContractId.set(contractId, [cmss])
      }
    }

    allContracts.forEach((contract: IContract) => {
      console.debug(`${fn} contract: ${contract.hrid}`)
      const contractCMSServices =
        countableCMSServicesByContractId.get(contract.id) ?? []
      console.debug(
        `${fn} contractCMSServices.length:`,
        contractCMSServices.length
      )
      contract.numCompanyMSServices = contractCMSServices.length
      contract.cmssStatusCounts = countUniqueStatii(contractCMSServices)

      // Set contract status and canBeDeleted based on numCompanyMSServices
      // ⚠ OPEN QUESTION — SA / Non-Contract audit, 2026-08-20. Deferred, not
      // settled: this gates the NO_SERVICES <-> DRAFT transition on SA ALONE,
      // so a Non-Contract never gets its status re-derived here. The
      // OPPORTUNITY cascade runs the same rule for every non-MSA contract
      // (_propagateFromOMSServicesToContracts), i.e. Non-Contract included.
      // Company and Order agree with each other and disagree with Opportunity.
      //
      // Aligning them needs additional logic rather than a one-line edit, so
      // the divergence stands for now. All three behaviours are pinned by the
      // co-located specs, so changing any one of them will fail loudly.
      if (contract.contractType.id === ContractTypeIds.SA) {
        console.debug(
          `${fn} contractType is SA. Going to consider` +
            ` numCompanyMSServices in setting status.`
        )
        if (!contract.numCompanyMSServices) {
          contract.status = getContractStatusById(ContractStatusIds.NO_SERVICES)
          contract.canBeDeleted = true
        } else if (contract.status.id === ContractStatusIds.NO_SERVICES) {
          contract.status = getContractStatusById(ContractStatusIds.DRAFT)
          contract.canBeDeleted = false
        }
      }
    })
    console.debug(`${fn} propagated to ${allContracts.length} contracts`)
  }

  //
  // execution starts here...
  //
  const fn = `${mn} propagateCMSServicesData |`
  console.debug(`${fn} called`)
  setUpCompanyMSServices()
  _propagateFromCMSServicesToCompany({
    mn,
    company: overviewData.company,
    allCMSServices,
  })
  _propagateFromCMSServicesToCMS({
    mn,
    allCMS: overviewData.companyMasterServices,
  })

  _propagateFromCMSServicesToContracts({
    mn,
    allContracts: overviewData.contracts,
    allCMSServices,
  })
}

function getTargetEntityStatusFromPropagateStatusArray<
  T extends IEntityDataItemWithStatus
>({
  propagateStatusArray,
  cmssArray,
  targetEntity,
  targetEntityGetStatusByIdFunction,
}: {
  propagateStatusArray: TPotentialStatus[]
  cmssArray: TMSService[]
  targetEntity: T
  targetEntityGetStatusByIdFunction: Function
}) {
  function _gotIdButNotObjectErrorHandler({
    potentialStatus,
    targetEntity,
  }: {
    potentialStatus: TPotentialStatus
    targetEntity: T
  }) {
    throw new Error(
      `${fn} target Entity '${targetEntity.hrid}' -` +
        ` determined id of status to set` +
        ` (${potentialStatus.statusIdForOnlyMssStatus}),` +
        ` but couldn't get status object based on it.` +
        ` This should never happen.`
    )
  }
  const fn = `${mn} getTargetEntityStatusFromPropagateStatusArray |`

  // Both predicates below are pure functions of the SET of distinct status ids
  // present, so one pass answers every candidate. This used to be six
  // iterations each running up to three `.some()` scans of the full array —
  // ~13 whole-array scans per master service, all computed eagerly even though
  // the loop underneath returns on the first match.
  // ⚠ Superseded rows contribute nothing, and this `continue` is the only thing
  // that makes it so. Left in, such a row would join the set and make
  // "something other than this status" true on every rung, pinning the parent
  // at a SOME_* status forever -- a wrong roll-up no user action could clear.
  //
  // ⚠ It matters MORE than it used to. The marker is an itemAvailabilityState
  // now, so the row carries a real, ordinary lifecycle status: drop this and it
  // contributes a plausible status rather than an obviously foreign one, and
  // the ladder comes out subtly wrong instead of visibly stuck.
  //
  // ⚠ The server does NOT do this. Its status-propagation trigger aggregates
  // over every row with no availability filter at all, so client and server can
  // disagree here; filed as WXTG/cynque-api-server#1569.
  const presentStatusIds = new Set<number>()
  for (const cmss of cmssArray) {
    if (isSupersededMSService(cmss)) {
      continue
    }
    presentStatusIds.add(cmss.status.id as number)
  }

  propagateStatusArray.forEach((potentialStatus) => {
    potentialStatus.didFindStatus =
      presentStatusIds.has(potentialStatus.statusId) ||
      (!!potentialStatus.alternateStatusId &&
        presentStatusIds.has(potentialStatus.alternateStatusId))
    // "Other than this status or a terminal one." DISCONNECTED is excluded so
    // that, say, ACTIVE + DISCONNECTED still resolves to ACTIVE rather than
    // SOME_ACTIVE. The `?? DEFINITELY_BAD_STATUS_ID` in the previous
    // implementation existed only to keep a real id out of the exclusion list
    // when there is no alternate; a Set built from the defined ids does the
    // same without the sentinel.
    const excludedStatusIds = new Set<number>([
      potentialStatus.statusId,
      CompanyMSServicesStatusIds.DISCONNECTED,
    ])
    if (potentialStatus.alternateStatusId) {
      excludedStatusIds.add(potentialStatus.alternateStatusId)
    }
    potentialStatus.didFindOtherThanStatusOrCancelled = [
      ...presentStatusIds,
    ].some((statusId) => !excludedStatusIds.has(statusId))
  })

  for (const potentialStatus of propagateStatusArray) {
    if (
      potentialStatus.didFindStatus &&
      !potentialStatus.didFindOtherThanStatusOrCancelled
    ) {
      const statusToSet = targetEntityGetStatusByIdFunction(
        potentialStatus.statusIdForOnlyMssStatus
      )
      if (!statusToSet) {
        _gotIdButNotObjectErrorHandler({ potentialStatus, targetEntity })
      }
      console.debug(`${fn} returning statusToSet ${statusToSet?.label}`)
      return statusToSet
    } else if (
      potentialStatus.didFindStatus &&
      potentialStatus.didFindOtherThanStatusOrCancelled
    ) {
      const statusToSet = targetEntityGetStatusByIdFunction(
        potentialStatus.statusIdForOtherFound
      )
      if (!statusToSet) {
        _gotIdButNotObjectErrorHandler({ potentialStatus, targetEntity })
      }
      console.debug(`${fn} returning statusToSet ${statusToSet?.label}`)
      return statusToSet
    }
  }
  throw new Error(
    `${fn} target Entity with hrid '${targetEntity.hrid}' status did not get set.` +
      ` This should never happen.`
  )
}
